import { useTranslation } from 'react-i18next';
import { SelectTriggerSkeleton } from '@/components/core';
import { SearchableSelect } from '@/components/core/searchable-select/searchable-select';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';

interface DepartmentFilterProps {
  /** The picked department id; '' means "all the departments the user may see". */
  value: string;
  onChange: (departmentId: string) => void;
  /** Applied to the trigger button, e.g. to match a surrounding filter bar's control height. */
  selectClassName?: string;
  /** A user limited to one department gets it pre-selected and locked (default). Turn off for a
   * list that does not force the department clause onto its query (see `ReviewFilters`), so the
   * picker never claims a filter that is not applied. */
  lockSingle?: boolean;
}

/**
 * The department picker every list shares. What it offers follows the user's department
 * permissions (see `useDepartmentScope`): every department for `VIEW_ALL_DEPARTMENTS`, otherwise
 * only the departments whose Head of Department role the user holds. A user limited to a single department gets it
 * pre-selected and locked. Pair it with `useDepartmentScope().getDepartmentIdCondition` when
 * building the list's query.
 */
export const DepartmentFilter = ({
  value,
  onChange,
  selectClassName,
  lockSingle = true,
}: DepartmentFilterProps) => {
  const { t } = useTranslation();
  const { departments, isLoading } = useDepartmentScope();

  // Until the scope is known the picker can't tell "All" from a locked single department.
  if (isLoading) return <SelectTriggerSkeleton className={selectClassName} />;

  const onlyDepartment = lockSingle && departments.length === 1 ? departments[0] : undefined;
  const options = [
    ...(onlyDepartment ? [] : [{ value: '', label: t('ALL_DEPARTMENT') }]),
    ...departments.map((department) => ({ value: department.ItemId, label: department.Title })),
  ];

  return (
    <SearchableSelect
      value={onlyDepartment ? onlyDepartment.ItemId : value}
      options={options}
      onChange={onChange}
      placeholder={t('DEPARTMENT')}
      searchPlaceholder={t('SEARCH')}
      emptyText={t('NO_RESULTS_FOUND')}
      disabled={Boolean(onlyDepartment)}
      className={selectClassName}
    />
  );
};
